/** TimeEntryFormModal — two-column create/edit modal (A5 layout).
 *
 * Model mapping (backend v2, docs/models/time-tracking-v2.md):
 * - Create: 时长 + 日期 → startTime = 当日 09:00 本地时间, endTime =
 *   startTime + 时长. The entry is always STOPPED — server-side RUNNING
 *   timers are started elsewhere (banner / list), never from this form.
 * - Local stopwatch: the ▶ button runs a client-side timer; stopping writes
 *   the elapsed hours back into 时长 (≥0.01h keeps 2 decimals, shorter runs
 *   keep 4 so a few seconds don't round away to 0).
 * - Edit: the backend PATCH only accepts matter/action/description/billing/
 *   private/rate (TimeEntryUpdate), so 时长/日期/录入人 are read-only here —
 *   an editable field the API silently ignores would be a lie. Editing a
 *   RUNNING entry shows the live elapsed since startTime (1 Hz refresh).
 * - `billed` entries are immutable (v2 §3.1): all fields disabled with a
 *   notice.
 * - The A5 "show on this bill" toggle has no backend field; 「私有记录」
 *   (is_private) replaces it — a real switch, not a fake one. */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import { CaretRightOutlined } from "@ant-design/icons";
import dayjs, { type Dayjs } from "dayjs";
import type {
  ActionType,
  TimeEntry,
  TimeEntryCreatePayload,
  TimeEntryUpdatePayload,
} from "../../types";
import {
  useCreateTimeEntry,
  useDeleteTimeEntry,
  useUpdateTimeEntry,
} from "../../api/queries";
import { useAuthStore } from "../../stores/auth";
import { toLocalIsoString } from "./utils";

const { Text } = Typography;

/** Create-mode anchor: entries land at 09:00 local on the chosen day. */
const CREATE_DEFAULT_HOUR = 9;
/** Stopwatch rounding threshold in hours: above → 2 decimals, below → 4. */
const TIMER_TWO_DECIMAL_HOURS = 0.01;
/** Column gutter, matching the --jerry-sp-4 (16px) spacing token. */
const COLUMN_GUTTER: [number, number] = [16, 0];

const BILLED_NOTICE = "已开票记录不可修改；如需更正，请联系管理员回退计费状态";
const PRIVATE_HINT = "仅您本人与租户管理员可见";

function formatHms(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hh = String(Math.floor(s / 3600)).padStart(2, "0");
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

/** Edit-mode duration prefill (hours). Falls back to end-start when
 *  duration_seconds is still 0 (RUNNING entries have no duration yet). */
function entryDurationHours(entry: TimeEntry): number | undefined {
  if (entry.durationSeconds > 0) {
    return Number((entry.durationSeconds / 3600).toFixed(2));
  }
  if (entry.endTime) {
    const span =
      (new Date(entry.endTime).getTime() - new Date(entry.startTime).getTime()) / 1000;
    if (span > 0) return Number((span / 3600).toFixed(2));
  }
  return undefined;
}

function filterByLabel(input: string, option?: { label?: string }): boolean {
  return (option?.label ?? "").toString().toLowerCase().includes(input.toLowerCase());
}

interface FormValues {
  durationHours?: number | null;
  matterId?: string | null;
  actionTypeId?: string | null;
  date?: Dayjs | null;
  employeeId?: string;
  description?: string;
  hourlyRate?: number | null;
  nonBillable?: boolean;
  isPrivate?: boolean;
}

export interface TimeEntryFormModalProps {
  open: boolean;
  /** Initial entry to edit. When provided, mode is "edit". */
  entry?: TimeEntry | null;
  /** Operator's default rate (from staff-profiles/me). */
  defaultHourlyRate?: number | null;
  matterOptions: { value: string; label: string }[];
  /** When editing an entry bound to a matter, lock the matter field. */
  lockedMatterId?: string;
  /** Allow choosing a different 录入人 (proxy entry). Default true. */
  allowProxy?: boolean;
  employeeOptions: { value: string; label: string }[];
  actionTypes: ActionType[];
  /** Create mode: start the local stopwatch as soon as the modal opens. */
  autoStartTimer?: boolean;
  onClose: () => void;
  /** Optional callback after a successful create/edit. */
  onSuccess?: (entry: TimeEntry) => void;
}

/** What the primary/secondary save buttons do after a successful create. */
type SaveIntent = "close" | "new" | "duplicate";

export default function TimeEntryFormModal({
  open,
  entry,
  defaultHourlyRate,
  matterOptions,
  lockedMatterId,
  allowProxy = true,
  employeeOptions,
  actionTypes,
  autoStartTimer = false,
  onClose,
  onSuccess,
}: TimeEntryFormModalProps) {
  const isEdit = !!entry;
  const billed = entry?.billingStatus === "billed";
  const isRunningEdit = isEdit && entry?.status === "running";
  const [form] = Form.useForm<FormValues>();
  const [messageApi, contextHolder] = message.useMessage();
  const createEntry = useCreateTimeEntry();
  const updateEntry = useUpdateTimeEntry();
  const deleteEntry = useDeleteTimeEntry();
  const currentUserId = useAuthStore((s) => s.user?.id);

  // Local stopwatch (create mode only).
  const timerStartRef = useRef<number | null>(null);
  const [timerRunning, setTimerRunning] = useState(false);
  const [nowTs, setNowTs] = useState(() => Date.now());

  const startTimer = useCallback(() => {
    timerStartRef.current = Date.now();
    setNowTs(timerStartRef.current);
    setTimerRunning(true);
  }, []);

  const stopTimer = useCallback(() => {
    const startedAt = timerStartRef.current;
    timerStartRef.current = null;
    setTimerRunning(false);
    if (startedAt == null) return;
    const hours = (Date.now() - startedAt) / 1000 / 3600;
    const rounded =
      hours >= TIMER_TWO_DECIMAL_HOURS ? Number(hours.toFixed(2)) : Number(hours.toFixed(4));
    form.setFieldValue("durationHours", rounded);
  }, [form]);

  // ---- open-time initialisation ----
  useEffect(() => {
    if (!open) return;
    if (isEdit && entry) {
      form.setFieldsValue({
        matterId: lockedMatterId ?? entry.matterId,
        actionTypeId: entry.actionTypeId ?? undefined,
        employeeId: entry.employeeId,
        description: entry.description ?? "",
        date: entry.startTime ? dayjs(entry.startTime) : undefined,
        durationHours: entryDurationHours(entry),
        hourlyRate: entry.hourlyRate,
        nonBillable: entry.billingStatus === "non_billable",
        isPrivate: entry.isPrivate,
      });
    } else {
      form.setFieldsValue({
        matterId: lockedMatterId ?? undefined,
        actionTypeId: undefined,
        employeeId: currentUserId,
        description: "",
        date: undefined,
        durationHours: undefined,
        hourlyRate: defaultHourlyRate ?? null,
        nonBillable: false,
        isPrivate: false,
      });
      if (autoStartTimer) startTimer();
    }
  }, [
    open,
    entry,
    isEdit,
    lockedMatterId,
    currentUserId,
    defaultHourlyRate,
    autoStartTimer,
    form,
    startTimer,
  ]);

  // ---- 1 Hz tick: stopwatch display + live elapsed for RUNNING edits ----
  const ticking = open && (timerRunning || isRunningEdit);
  useEffect(() => {
    if (!ticking) return;
    const t = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [ticking]);

  // Modal closed → drop the stopwatch so reopening starts clean.
  useEffect(() => {
    if (open) return;
    timerStartRef.current = null;
    setTimerRunning(false);
  }, [open]);

  // RUNNING edit: the read-only 时长 mirrors live elapsed since startTime.
  useEffect(() => {
    if (!open || !isRunningEdit || !entry) return;
    const elapsed = (nowTs - new Date(entry.startTime).getTime()) / 1000;
    form.setFieldValue("durationHours", Math.max(0, Number((elapsed / 3600).toFixed(2))));
  }, [open, isRunningEdit, entry, nowTs, form]);

  const handleSubmit = async (intent: SaveIntent) => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    if (isEdit && entry) {
      // PATCH carries only the mutable attribute set (see header comment).
      const payload: TimeEntryUpdatePayload = {
        matterId: values.matterId ?? null,
        actionTypeId: values.actionTypeId ?? null,
        description: values.description,
        billingStatus: billed
          ? entry.billingStatus
          : values.nonBillable
            ? "non_billable"
            : "billable",
        isPrivate: values.isPrivate ?? false,
        hourlyRate: values.hourlyRate ?? null,
      };
      try {
        const res = await updateEntry.mutateAsync({ id: entry.id, data: payload });
        messageApi.success("已保存");
        onSuccess?.(res.data);
        onClose();
      } catch {
        messageApi.error("保存失败");
      }
      return;
    }

    const start = values
      .date!.hour(CREATE_DEFAULT_HOUR)
      .minute(0)
      .second(0)
      .millisecond(0);
    const end = start.add(Math.round(values.durationHours! * 3600 * 1000), "millisecond");
    const payload: TimeEntryCreatePayload = {
      matterId: values.matterId ?? null,
      actionTypeId: values.actionTypeId ?? null,
      employeeId: values.employeeId,
      description: values.description,
      billingStatus: values.nonBillable ? "non_billable" : "billable",
      isPrivate: values.isPrivate ?? false,
      hourlyRate: values.hourlyRate ?? null,
      startTime: toLocalIsoString(start.toDate()),
      endTime: toLocalIsoString(end.toDate()),
    };
    try {
      const res = await createEntry.mutateAsync(payload);
      onSuccess?.(res.data);
      if (intent === "new") {
        messageApi.success("已保存，可继续录入");
        // Reset per-entry inputs; keep 事项/日期/录入人/费率 for the next one.
        timerStartRef.current = null;
        setTimerRunning(false);
        form.setFieldsValue({
          description: "",
          durationHours: undefined,
          actionTypeId: undefined,
        });
      } else if (intent === "duplicate") {
        messageApi.success("已保存，表单已保留当前内容");
      } else {
        messageApi.success("已保存");
        onClose();
      }
    } catch {
      messageApi.error("保存失败");
    }
  };

  const handleDelete = async () => {
    if (!entry) return;
    try {
      await deleteEntry.mutateAsync(entry.id);
      messageApi.success("已删除");
      onClose();
    } catch {
      messageApi.error("删除失败");
    }
  };

  const handleClose = () => {
    if (createEntry.isPending || updateEntry.isPending || deleteEntry.isPending) return;
    onClose();
  };

  const saving = createEntry.isPending || updateEntry.isPending;
  const stopwatchSeconds =
    timerStartRef.current != null ? (nowTs - timerStartRef.current) / 1000 : 0;

  return (
    <Modal
      data-testid="time-entry-form-modal"
      title={isEdit ? "编辑工时记录" : "新建工时记录"}
      open={open}
      onCancel={handleClose}
      width={720}
      destroyOnHidden
      footer={
        <Space>
          <Button
            data-testid="time-entry-save"
            type="primary"
            loading={saving}
            onClick={() => handleSubmit("close")}
          >
            保存并关闭
          </Button>
          {!isEdit && (
            <>
              <Button
                data-testid="time-entry-save-new"
                disabled={saving}
                onClick={() => handleSubmit("new")}
              >
                保存并新建
              </Button>
              <Button
                data-testid="time-entry-save-dup"
                disabled={saving}
                onClick={() => handleSubmit("duplicate")}
              >
                保存并复制
              </Button>
            </>
          )}
          <Button data-testid="time-entry-cancel" onClick={handleClose}>
            取消
          </Button>
          {isEdit && (
            <Popconfirm
              title="删除该工时记录？"
              okText="删除"
              cancelText="取消"
              okButtonProps={{ danger: true }}
              onConfirm={handleDelete}
            >
              <Button
                data-testid="time-entry-delete"
                type="primary"
                danger
                loading={deleteEntry.isPending}
              >
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      }
    >
      {contextHolder}
      {billed && (
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          {BILLED_NOTICE}
        </Text>
      )}
      <Form form={form} layout="vertical">
        <Row gutter={COLUMN_GUTTER}>
          <Col span={12}>
            {/* Nested noStyle Field: Form.Item binds to its direct child, and
                Space.Compact is not a form control — binding must target the
                InputNumber itself (same pattern as the antd Compact docs). */}
            <Form.Item label="时长" required={!isEdit}>
              <Space.Compact block>
                <Form.Item
                  name="durationHours"
                  noStyle
                  rules={
                    isEdit
                      ? []
                      : [
                          {
                            validator: (_: unknown, v: number | null | undefined) =>
                              v != null && v > 0
                                ? Promise.resolve()
                                : Promise.reject(new Error("请填写时长")),
                          },
                        ]
                  }
                >
                  <InputNumber
                    data-testid="time-entry-duration"
                    min={0}
                    step={0.1}
                    placeholder="1.5"
                    suffix="小时"
                    style={{ width: "100%" }}
                    disabled={billed || isEdit}
                  />
                </Form.Item>
                {!isEdit && (
                  <Button
                    data-testid="time-entry-timer-btn"
                    icon={<CaretRightOutlined />}
                    onClick={timerRunning ? stopTimer : startTimer}
                  >
                    {timerRunning ? formatHms(stopwatchSeconds) : "计时"}
                  </Button>
                )}
              </Space.Compact>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="matterId" label="事项">
              <Select
                data-testid="time-entry-matter"
                allowClear
                showSearch
                placeholder="按事项名称或客户查找"
                disabled={!!lockedMatterId || billed}
                options={matterOptions}
                filterOption={filterByLabel}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="actionTypeId" label="活动类别">
              <Select
                data-testid="time-entry-action-type"
                allowClear
                placeholder="按类别查找"
                disabled={billed}
                options={actionTypes.map((a) => ({ value: a.id, label: a.name }))}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="date"
              label="日期"
              required={!isEdit}
              rules={isEdit ? [] : [{ required: true, message: "请选择日期" }]}
            >
              <DatePicker
                data-testid="time-entry-date"
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                disabled={billed || isEdit}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="description" label="描述">
              <Input.TextArea
                data-testid="time-entry-description"
                rows={3}
                maxLength={500}
                placeholder="本次工作内容（可选）"
                disabled={billed}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="employeeId"
              label="录入人"
              required
              tooltip={allowProxy ? "默认是自己；代记他人时选对方" : undefined}
              rules={[{ required: true, message: "请选择录入人" }]}
            >
              <Select
                data-testid="time-entry-employee"
                showSearch
                placeholder="默认为当前用户"
                disabled={billed || isEdit || !allowProxy}
                options={employeeOptions}
                filterOption={filterByLabel}
              />
            </Form.Item>
          </Col>

          <Col span={12} />

          <Col span={12}>
            <Form.Item
              name="hourlyRate"
              label="费率"
              required
              rules={[{ required: true, message: "请填写费率" }]}
              extra={
                defaultHourlyRate != null && !billed ? (
                  <Button
                    type="link"
                    size="small"
                    data-testid="time-entry-rate-default-link"
                    style={{ padding: 0, height: "auto" }}
                    onClick={() => form.setFieldValue("hourlyRate", defaultHourlyRate)}
                  >
                    使用默认费率
                  </Button>
                ) : undefined
              }
            >
              <InputNumber
                data-testid="time-entry-rate"
                min={0}
                prefix="¥"
                suffix="元/小时"
                style={{ width: "100%" }}
                disabled={billed}
              />
            </Form.Item>
          </Col>
        </Row>

        <Space size="large" style={{ marginTop: 8 }}>
          <Form.Item name="nonBillable" valuePropName="checked" style={{ marginBottom: 0 }}>
            <Checkbox data-testid="time-entry-non-billable" disabled={billed}>
              非计费
            </Checkbox>
          </Form.Item>
          <Form.Item name="isPrivate" valuePropName="checked" style={{ marginBottom: 0 }}>
            <Checkbox data-testid="time-entry-private" title={PRIVATE_HINT} disabled={billed}>
              私有记录
            </Checkbox>
          </Form.Item>
        </Space>
      </Form>
    </Modal>
  );
}
